import {
  CustomOptionsStatus,
  GuardrailSettingsState,
  LLMSettings,
  CustomPromptCheckSettings,
  ExternalCheckSettingsInput,
  RegexCheckSettings,
  RegexFlag,
} from '../../../../../gamma-api';
import { isValidJSON } from '../../../../utils/stringUtil';
import { CheckFieldValueFnMap } from '../../../../utils/validationUtil';
import { CheckTypeValue, GuardrailCheckSettings, ICheckStep, isSettingsEnabled } from '../shared/utils';
import { validateRegexPatterns } from '../shared/regexValidation';

export type CheckValidationForm = {
  type?: CheckTypeValue;
  state?: GuardrailSettingsState;
  llm?: LLMSettings | null;
  prompt?: string;
  apiUrl?: string;
  patterns?: string[];
  flags?: RegexFlag[];
  customOptionsStatus?: CustomOptionsStatus;
  customOptionsJson?: string;
};

export type CheckValidationField = keyof CheckValidationForm;

const isValidationActive = (allFormValues: CheckValidationForm) => isSettingsEnabled(allFormValues.state);

export const checkFieldValueFnMap: CheckFieldValueFnMap<CheckValidationForm> = {
  type: () => true,
  state: () => true,
  llm: (value, allFormValues) => {
    if (!isValidationActive(allFormValues) || allFormValues.type !== CheckTypeValue.CustomPromptCheck) {
      return true;
    }

    return !!value?.provider_id && !!value?.model_name;
  },
  prompt: (value, allFormValues) => {
    if (!isValidationActive(allFormValues) || allFormValues.type !== CheckTypeValue.CustomPromptCheck) {
      return true;
    }

    return !!value?.trim();
  },
  apiUrl: (value, allFormValues) => {
    if (!isValidationActive(allFormValues) || allFormValues.type !== CheckTypeValue.ExternalCheck) {
      return true;
    }

    return !!value?.trim();
  },
  patterns: (value, allFormValues) => {
    if (!isValidationActive(allFormValues) || allFormValues.type !== CheckTypeValue.RegexCheck) {
      return true;
    }

    return validateRegexPatterns(value, allFormValues.flags);
  },
  flags: () => true,
  customOptionsStatus: () => true,
  customOptionsJson: (value, allFormValues) => {
    if (
      !isValidationActive(allFormValues) ||
      allFormValues.type !== CheckTypeValue.ExternalCheck ||
      allFormValues.customOptionsStatus !== CustomOptionsStatus.Enabled
    ) {
      return true;
    }

    return isValidJSON(value, { allowEmpty: true, objectOnly: true });
  },
};

const FIELD_ORDER_BY_TYPE: Record<CheckTypeValue, CheckValidationField[]> = {
  [CheckTypeValue.CustomPromptCheck]: ['llm', 'prompt'],
  [CheckTypeValue.ExternalCheck]: ['apiUrl', 'customOptionsJson'],
  [CheckTypeValue.RegexCheck]: ['patterns'],
};

export const getFirstInvalidCheckField = (values: CheckValidationForm): CheckValidationField | undefined => {
  if (!values.type) {
    return undefined;
  }

  return FIELD_ORDER_BY_TYPE[values.type].find((field) => checkFieldValueFnMap[field](values[field], values) !== true);
};

export const buildCheckValidationForm = (step?: ICheckStep): CheckValidationForm => {
  const check = step?.check as GuardrailCheckSettings | undefined;

  if (check?.type === CheckTypeValue.CustomPromptCheck) {
    const customPromptCheck = check as CustomPromptCheckSettings;

    return {
      type: CheckTypeValue.CustomPromptCheck,
      state: customPromptCheck.state,
      llm: customPromptCheck.llm,
      prompt: customPromptCheck.prompt,
      apiUrl: '',
      patterns: [],
      flags: [],
      customOptionsStatus: CustomOptionsStatus.Disabled,
      customOptionsJson: '',
    };
  }

  if (check?.type === CheckTypeValue.ExternalCheck) {
    const externalCheck = check as ExternalCheckSettingsInput;

    return {
      type: CheckTypeValue.ExternalCheck,
      state: externalCheck.state,
      llm: null,
      prompt: '',
      apiUrl: externalCheck.api_url,
      patterns: [],
      flags: [],
      customOptionsStatus: externalCheck.custom_options?.status,
      customOptionsJson: externalCheck.custom_options?.json_value,
    };
  }

  if (check?.type === CheckTypeValue.RegexCheck) {
    const regexCheck = check as RegexCheckSettings;

    return {
      type: CheckTypeValue.RegexCheck,
      state: regexCheck.state,
      llm: null,
      prompt: '',
      apiUrl: '',
      patterns: regexCheck.patterns,
      flags: regexCheck.flags,
      customOptionsStatus: CustomOptionsStatus.Disabled,
      customOptionsJson: '',
    };
  }

  return {
    type: undefined,
    state: GuardrailSettingsState.Enabled,
    llm: null,
    prompt: '',
    apiUrl: '',
    patterns: [],
    flags: [],
    customOptionsStatus: CustomOptionsStatus.Disabled,
    customOptionsJson: '',
  };
};
